import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'umi';
import http from '@/utils/http';
import { setCustomFilterList } from '../components/NewFilter/customFilterList';
import { getSelectorMap } from '../_config/filterListMap';
import { IDashboardSettingState } from '../types/dashboard';
import { formatSortFiltersData } from '../utils/_config/formatSortFiltersData';
import { getMediaChannelIcon, mediaChannelMap } from '@/pages/home/_config/mediaChannal';
import Default from '@/pages/home/assets/svg/dashboard/default.svg';
import { getSystemFlag } from '@/pages/dashboard/pages/overview/_config/system';

/**
 * 用于获取外部筛选器数据的 Hook
 * @param selectorApi - 外部筛选器 API 地址
 * @returns 返回外部筛选器数据和加载状态
 * 
 * @example
 * ```tsx
 * const RealtimeRevenueFilter = (props) => {
 *   const { selectorApi } = props;
 *   const { selectorData, selectorLoading } = useOutSelector({ selectorApi });
 *   
 *   return (
 *     <Filter 
 *       {...(selectorApi ? {
 *         outSelector: selectorData,
 *         loading: selectorLoading,
 *       } : {})}
 *       {...otherProps}
 *     />
 *   );
 * };
 * ```
 */

interface IUseOutSelectorProps {
  selectorApi?: string;
  extraSelectorParams?: Record<string, unknown>;
}

interface ICustomFilter {
  id: string;
  name: string;
  // 其他已知属性
}

interface ISelectorResponse {
  custom_filters?: Record<string, ICustomFilter>;
  game_code?: string;
  os_list?: Array<{os_id: string; os_name: string;}>;
  [key: string]: any;
}

const useOutSelector = (props: IUseOutSelectorProps) => {
  const { selectorApi, extraSelectorParams } = props || {};
  const dispatch = useDispatch();
  const [selectorData, setSelectorData] = useState<ISelectorResponse>({});
  const [selectorLoading, setSelectorLoading] = useState<boolean>(true);

  const { gameCode } = useSelector((state: IDashboardSettingState) => {
    return {
      gameCode: state?.dashboard?.currentGame?.game_code,
    };
  }, shallowEqual);

  useEffect(() => {
    if (!selectorApi) {
      setSelectorLoading(false);
      return;
    }

    setSelectorLoading(true);
    http({
      url: selectorApi,
      method: 'post',
      data: {
        game_code: gameCode,
        ...(extraSelectorParams || {}),
      },
    })
      .then((res: any) => {
        const data = {
          gameCode,
          ...(res || {}),
          ...(res?.custom_filters || {}),
        };
        if (data?.os_list) {
          data.os_list = data.os_list.map((item: any) => {
            const cleanedEnKey = (item.os_name_en || '').toLowerCase().replace(/\s+/g, '');
            item.icon = getSystemFlag(cleanedEnKey) || Default;
            return item;
          });
        }
        if (data?.ua_network) {
          data.ua_network = data.ua_network.map((item: any) => {
            const channelIcon = getMediaChannelIcon(item.name_en || item.code || '');
            item.icon = channelIcon === mediaChannelMap._default ? Default : channelIcon;
            return item;
          });
        }
        // 自定义筛选器列表
        const customFiltersKey = Object.keys(res?.custom_filters || {});
        setCustomFilterList(customFiltersKey);
        dispatch({
          type: 'dashboardSetting/save',
          payload: {
            selector: formatSortFiltersData(data || {}),
            selectorMap: getSelectorMap(data),
          },
        });
        setSelectorData(data || {});
      })
      .catch((error: Error) => {
        console.error('获取外部筛选器数据失败:', error);
        setSelectorData({});
      })
      .finally(() => {
        setSelectorLoading(false);
      });
  }, [selectorApi, gameCode]);

  return {
    selectorApi,
    selectorData,
    selectorLoading,
  };
};

export default useOutSelector;

